"use client";

import { useEffect, useRef, useState } from "react";
import type { PbMapLocation } from "../types";
import { attrsToProps } from "../render/attrs";

type Props = {
  attrs: Record<string, string>;
  locations: PbMapLocation[];
  showControls: boolean;
  googleMapsApiKey?: string;
  googleMapsStyle?: unknown[];
  labels?: { phone: string };
};

type GoogleNs = {
  maps: {
    Map: new (el: HTMLElement, opts: Record<string, unknown>) => {
      fitBounds(b: unknown): void;
      setCenter(c: unknown): void;
      setZoom(z: number): void;
    };
    Marker: new (opts: Record<string, unknown>) => { addListener(ev: string, cb: () => void): void };
    InfoWindow: new (opts: Record<string, unknown>) => { open(opts: Record<string, unknown>): void; close(): void };
    LatLngBounds: new () => { extend(p: unknown): void };
    MapTypeControlStyle: { DEFAULT: unknown };
  };
};

declare global {
  interface Window {
    google?: GoogleNs;
    __pbGoogleMapsLoading?: Promise<GoogleNs>;
  }
}

function loadGoogleMaps(apiKey: string): Promise<GoogleNs> {
  if (window.google?.maps) return Promise.resolve(window.google);
  if (window.__pbGoogleMapsLoading) return window.__pbGoogleMapsLoading;
  window.__pbGoogleMapsLoading = new Promise<GoogleNs>((resolve, reject) => {
    const callback = `__pbGoogleMapsReady${Date.now()}`;
    (window as unknown as Record<string, () => void>)[callback] = () => {
      if (window.google) resolve(window.google);
      else reject(new Error("Google Maps failed to initialise"));
    };
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&v=quarterly&loading=async&callback=${callback}`;
    script.async = true;
    script.onerror = () => reject(new Error("Google Maps script failed to load"));
    document.head.appendChild(script);
  });
  return window.__pbGoogleMapsLoading;
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

/** Info window markup, same structure as Magento `utils/map.js`. */
export function infoWindowHtml(loc: PbMapLocation, phoneLabel: string): string {
  const e = (v?: string) => (v ? escapeHtml(v) : "");
  const comment = loc.comment ? `<p>${e(loc.comment).replace(/\n/g, "<br/>")}</p>` : "";
  const phone = loc.phone ? `<p>${escapeHtml(phoneLabel)}: <span>${e(loc.phone)}</span></p>` : "";
  const address = loc.address ? `${e(loc.address)}<br/>` : "";
  const cityLine = [e(loc.city), [e(loc.state), e(loc.zipcode)].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  const country = loc.country ? `<br/>${e(loc.country)}` : "";
  return `<div class="pb-map-info"><h3><b>${e(loc.name)}</b></h3>${comment}${phone}<p><span>${address}${cityLine}${country}</span></p></div>`;
}

/**
 * Page Builder Map. Native behaviour (Magento `map/appearance/default/widget.js`):
 * zoom 8 for one location, `fitBounds` for several, info window with name,
 * comment, phone and address, controls toggled by `data-show-controls`, map
 * hidden when there are no locations.
 *
 * Google Maps is used when `NEXT_PUBLIC_PAGEBUILDER_GOOGLE_MAPS_API_KEY` is
 * set; otherwise the same locations render on OpenStreetMap through Leaflet
 * (already a storefront dependency), so the content type never shows an
 * empty box. The map library loads only when the element nears the viewport.
 */
export function PbMap({ attrs, locations, showControls, googleMapsApiKey, googleMapsStyle, labels }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  const phoneLabel = labels?.phone ?? "Phone";

  useEffect(() => {
    const el = ref.current;
    if (!el || locations.length === 0) return;
    let cancelled = false;
    let cleanup: (() => void) | undefined;

    const init = async () => {
      try {
        if (googleMapsApiKey) {
          const google = await loadGoogleMaps(googleMapsApiKey);
          if (cancelled) return;
          const map = new google.maps.Map(el, {
            zoom: 8,
            center: { lat: locations[0].lat, lng: locations[0].lng },
            scrollwheel: false,
            disableDoubleClickZoom: false,
            disableDefaultUI: !showControls,
            mapTypeControl: showControls,
            mapTypeControlOptions: { style: google.maps.MapTypeControlStyle.DEFAULT },
            styles: googleMapsStyle ?? [],
          });
          let open: { close(): void } | null = null;
          const bounds = new google.maps.LatLngBounds();
          for (const loc of locations) {
            const position = { lat: loc.lat, lng: loc.lng };
            bounds.extend(position);
            const marker = new google.maps.Marker({ map, position, title: loc.name });
            if (loc.name) {
              const info = new google.maps.InfoWindow({ content: infoWindowHtml(loc, phoneLabel), maxWidth: 350 });
              marker.addListener("click", () => {
                open?.close();
                info.open({ map, anchor: marker });
                open = info;
              });
            }
          }
          if (locations.length > 1) map.fitBounds(bounds);
          return;
        }

        const { mountLeafletMap } = await import("./pbLeafletMap");
        if (cancelled) return;
        cleanup = mountLeafletMap(el, locations, showControls, (loc) => infoWindowHtml(loc, phoneLabel));
      } catch {
        if (!cancelled) setFailed(true);
      }
    };

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect();
          void init();
        }
      },
      { rootMargin: "300px 0px" }
    );
    io.observe(el);
    return () => {
      cancelled = true;
      io.disconnect();
      cleanup?.();
    };
  }, [locations, showControls, googleMapsApiKey, googleMapsStyle, phoneLabel]);

  if (locations.length === 0) return null;

  return (
    <div {...attrsToProps(attrs, "pb-map")} ref={ref} data-pb-map-failed={failed ? "" : undefined}>
      <noscript>
        <ul>
          {locations.map((l, i) => (
            <li key={i}>{[l.name, l.address, l.city].filter(Boolean).join(", ")}</li>
          ))}
        </ul>
      </noscript>
    </div>
  );
}
